import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Search01Icon,
  SparklesIcon,
  ArrowRight01Icon,
  BookOpen01Icon,
  Idea01Icon,
  AiSearchIcon,
  SecurityCheckIcon,
  CustomerSupportIcon,
  Task01Icon,
  Calendar03Icon,
  Rocket01Icon,
  ChartHistogramIcon,
  AlertCircleIcon,
  AiInnovation01Icon,
  BookmarkCheck01Icon,
  Settings02Icon,
} from '@hugeicons/core-free-icons';
import { MotionSection, StaggerContainer, StaggerItem } from '@/components/shared/MotionSection';
import { DemoDataBadge } from '@/components/shared/DemoDataBadge';
import { UcChristusLogo } from '@/components/brand/InstitutionalBrand';
import { cn } from '@/lib/utils';

const SUGGESTIONS = [
  { label: 'Aprender sobre IA', icon: BookOpen01Icon, to: '/kb' },
  { label: 'Registrar idea', icon: Idea01Icon, to: '/registro/nueva' },
  { label: 'Crear un prompt seguro', icon: AiSearchIcon, to: '/sandbox' },
  { label: 'Evaluar herramienta', icon: SecurityCheckIcon, to: '/registro' },
  { label: 'Pedir ayuda', icon: CustomerSupportIcon, to: '/adopcion' },
];

export default function MyVIA() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState(null);

  useEffect(() => {
    api
      .get('/dashboard')
      .then(({ data }) => setStats(data?.totals))
      .catch(() => {});
    api
      .get('/tasks')
      .then(({ data }) => setTasks(data?.slice(0, 3)))
      .catch(() => {});
  }, []);

  const greeting = getGreeting();

  return (
    <div>
      {/* ── HERO — full-width, estilo KB ── */}
      <section className="relative overflow-hidden bg-gradient-hero" aria-labelledby="myvia-greeting">
        <div className="absolute inset-0 opacity-60" aria-hidden="true">
          <svg viewBox="0 0 1440 600" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
            <defs>
              <linearGradient id="hw1" x1="0%" y1="0%" x2="100%" y2="50%">
                <stop offset="0%" stopColor="#671E75" stopOpacity="0.9" />
                <stop offset="50%" stopColor="#307FE2" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.3" />
              </linearGradient>
              <linearGradient id="hw2" x1="100%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#307FE2" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#671E75" stopOpacity="0.6" />
              </linearGradient>
            </defs>
            <path d="M0,300 Q360,200 720,300 T1440,250" fill="none" stroke="url(#hw1)" strokeWidth="2.5" opacity="0.7">
              <animate
                attributeName="d"
                values="M0,300 Q360,200 720,300 T1440,250;M0,280 Q360,350 720,260 T1440,300;M0,300 Q360,200 720,300 T1440,250"
                dur="10s"
                repeatCount="indefinite"
              />
            </path>
            <path d="M0,350 Q360,280 720,350 T1440,320" fill="none" stroke="url(#hw1)" strokeWidth="1.5" opacity="0.5">
              <animate
                attributeName="d"
                values="M0,350 Q360,280 720,350 T1440,320;M0,330 Q360,400 720,310 T1440,360;M0,350 Q360,280 720,350 T1440,320"
                dur="13s"
                repeatCount="indefinite"
              />
            </path>
            <path d="M0,400 Q480,320 960,400 T1440,370" fill="none" stroke="url(#hw2)" strokeWidth="1.5" opacity="0.4">
              <animate
                attributeName="d"
                values="M0,400 Q480,320 960,400 T1440,370;M0,380 Q480,440 960,360 T1440,410;M0,400 Q480,320 960,400 T1440,370"
                dur="16s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="flex items-center gap-2 mb-6">
            <UcChristusLogo contrast="light" className="hidden h-5 opacity-90 sm:block" priority />
            <span className="hidden h-5 w-px bg-white/20 sm:block" aria-hidden="true" />
            <span className="text-sm font-medium text-purple-100">VIA — Valor de la IA</span>
            <DemoDataBadge className="ml-1 bg-white/10 border-white/15 text-white/70" />
          </div>
          <h1
            id="myvia-greeting"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] max-w-3xl">
            {greeting},{' '}
            <span className="bg-gradient-to-r from-violet-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">
              {user?.name?.split(' ')[0]}
            </span>
          </h1>
          <p className="text-lg text-white/50 mt-6 max-w-2xl leading-relaxed">
            ¿En qué puedo ayudarte hoy? Explora recursos, registra ideas o consulta el estado de tus iniciativas.
          </p>

          {/* Search */}
          <div className="relative mt-10 max-w-xl">
            <HugeiconsIcon
              icon={Search01Icon}
              size={20}
              className="absolute left-5 top-1/2 -translate-y-1/2 text-white/40"
              aria-hidden="true"
            />
            <Input
              placeholder="Buscar artículos, prompts, herramientas..."
              className="pl-13 h-14 bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-2xl text-base backdrop-blur-sm focus:bg-white/10 focus:border-white/20 transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && search.trim()) {
                  window.location.href = `/kb/buscar?q=${encodeURIComponent(search)}`;
                }
              }}
              aria-label="Buscar en VIA"
            />
          </div>

          {/* Suggestion chips */}
          <div className="flex flex-wrap gap-2 mt-5">
            {SUGGESTIONS.map((s) => (
              <Link
                key={s.label}
                to={s.to}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-white/80 text-sm font-medium bg-white/5 hover:bg-white/10 backdrop-blur-sm transition-colors">
                <HugeiconsIcon icon={s.icon} size={15} aria-hidden="true" />
                {s.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTENT — contained ── */}
      <div className="max-w-7xl mx-auto px-6 space-y-10 py-12">
        {/* Quick stats */}
        {stats && (
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StaggerItem>
              <QuickStat icon={AiInnovation01Icon} label="Iniciativas" value={stats.initiatives} to="/registro" />
            </StaggerItem>
            <StaggerItem>
              <QuickStat
                icon={BookmarkCheck01Icon}
                label="Evaluaciones abiertas"
                value={stats.open_assessments}
                to="/registro"
              />
            </StaggerItem>
            <StaggerItem>
              <QuickStat icon={ChartHistogramIcon} label="En producción" value={stats.production} to="/valor" />
            </StaggerItem>
            <StaggerItem>
              <QuickStat
                icon={AlertCircleIcon}
                label="Shadow AI"
                value={stats.shadow_detected}
                to="/shadow-ai"
                variant="warning"
              />
            </StaggerItem>
          </StaggerContainer>
        )}

        {!stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-28 rounded-2xl" />
            ))}
          </div>
        )}

        {/* Tasks in progress */}
        <MotionSection delay={0.1}>
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-extrabold">Tus tareas pendientes</h2>
              <Link
                to="/tareas"
                className="text-xs text-[#671E75] font-semibold hover:underline flex items-center gap-1">
                Ver todas
                <HugeiconsIcon icon={ArrowRight01Icon} size={12} aria-hidden="true" />
              </Link>
            </div>
            {tasks === null ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-18 rounded-2xl" />
                ))}
              </div>
            ) : tasks.length === 0 ? (
              <Card className="border-border/50 rounded-2xl">
                <CardContent className="p-8 text-center">
                  <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                    <HugeiconsIcon icon={SparklesIcon} size={20} className="text-emerald-600" aria-hidden="true" />
                  </div>
                  <p className="text-sm text-muted-foreground">No tienes tareas pendientes. ¡Buen trabajo!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {tasks.map((t) => (
                  <Link
                    key={t.id}
                    to={t.link || `/iniciativas/${t.initiative_id}`}
                    className="flex items-center gap-4 rounded-2xl border border-border/50 bg-card p-4 transition-all duration-300 hover:shadow-[0_20px_60px_-15px_rgba(103,30,117,0.12)] hover:-translate-y-0.5 hover:border-[#671E75]/25 group">
                    <div className="h-10 w-10 rounded-full bg-[#307FE2] shadow-lg shadow-[#307FE2]/25 flex items-center justify-center shrink-0">
                      <HugeiconsIcon icon={Task01Icon} size={18} className="text-white" aria-hidden="true" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold line-clamp-2 group-hover:text-[#671E75] transition-colors">
                        {t.title}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {t.initiative_name && (
                          <p className="text-xs text-muted-foreground truncate">{t.initiative_name}</p>
                        )}
                        {t.due_date && (
                          <span className="text-[11px] text-muted-foreground flex items-center gap-1 shrink-0">
                            <HugeiconsIcon icon={Calendar03Icon} size={12} aria-hidden="true" />
                            {new Date(t.due_date).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}
                          </span>
                        )}
                      </div>
                    </div>
                    <HugeiconsIcon
                      icon={ArrowRight01Icon}
                      size={14}
                      className="text-muted-foreground shrink-0 group-hover:text-[#671E75] group-hover:translate-x-0.5 transition-all"
                      aria-hidden="true"
                    />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </MotionSection>

        {/* Quick access grid — estilo KB category cards */}
        <MotionSection delay={0.15}>
          <div>
            <h2 className="font-display text-lg font-extrabold mb-2">Acceso rápido</h2>
            <p className="text-sm text-muted-foreground mb-5">Atajos a las secciones que más usas en VIA.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              <QuickAccessCard
                icon={BookOpen01Icon}
                title="Knowledge Hub"
                description="Guías, prompts y buenas prácticas para adoptar IA de forma segura"
                to="/kb"
              />
              <QuickAccessCard
                icon={Rocket01Icon}
                title="Registrar iniciativa"
                description="Formaliza una nueva idea o proyecto de inteligencia artificial"
                to="/registro/nueva"
              />
              <QuickAccessCard
                icon={Settings02Icon}
                title="Herramientas autorizadas"
                description="Catálogo de herramientas aprobadas para tu rol"
                to="/herramientas"
              />
              <QuickAccessCard
                icon={CustomerSupportIcon}
                title="Academia y comunidad"
                description="Rutas por rol, certificaciones, champions y espacios de ayuda"
                to="/adopcion"
              />
              <QuickAccessCard
                icon={AiSearchIcon}
                title="Sandbox seguro"
                description="Crea prompts con datos sintéticos y controles de fast lane"
                to="/sandbox"
              />
              <QuickAccessCard
                icon={ChartHistogramIcon}
                title="Madurez y hoja de ruta"
                description="Capacidades, evidencia, brechas e integraciones institucionales"
                to="/madurez"
              />
            </div>
          </div>
        </MotionSection>
      </div>
    </div>
  );
}

function QuickStat({ icon, label, value, to, variant = 'default' }) {
  const iconBg = variant === 'warning' ? 'bg-amber-500 shadow-amber-500/25' : 'bg-primary shadow-primary/25';

  return (
    <Link
      to={to}
      className="block h-full rounded-2xl border border-border/50 bg-card p-5 transition-all duration-300 hover:shadow-float hover:-translate-y-1 hover:border-primary/30 group"
      aria-label={`${label}: ${value}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
        <div className={cn('size-9 shrink-0 rounded-full shadow-lg flex items-center justify-center', iconBg)}>
          <HugeiconsIcon icon={icon} size={16} className="text-white" aria-hidden="true" />
        </div>
      </div>
      <span className="font-display text-3xl font-extrabold tabular text-foreground">{value ?? '—'}</span>
    </Link>
  );
}

function QuickAccessCard({ icon, title, description, to }) {
  return (
    <Link to={to} className="block group h-full">
      <div className="rounded-2xl border border-border/50 bg-card overflow-visible transition-all duration-300 hover:shadow-float hover:-translate-y-1 hover:border-primary/30 h-full flex flex-col">
        {/* Icon */}
        <div className="px-5 pt-5">
          <div className="h-12 w-12 rounded-full bg-primary shadow-lg shadow-primary/25 flex items-center justify-center">
            <HugeiconsIcon icon={icon} size={20} className="text-primary-foreground" aria-hidden="true" />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 px-5 pt-3 pb-5 flex flex-col">
          <h3 className="font-display text-[15px] font-bold text-foreground leading-snug group-hover:text-primary-glow transition-colors">
            {title}
          </h3>
          <p className="text-[13px] text-muted-foreground mt-1.5 leading-relaxed line-clamp-2 flex-1">{description}</p>
          <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary-glow mt-4 group-hover:gap-2.5 transition-all duration-200">
            Explorar{' '}
            <HugeiconsIcon
              icon={ArrowRight01Icon}
              size={14}
              className="group-hover:translate-x-0.5 transition-transform"
              aria-hidden="true"
            />
          </span>
        </div>
      </div>
    </Link>
  );
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Buenos días';
  if (h < 18) return 'Buenas tardes';
  return 'Buenas noches';
}
